import { ColumnPicker, useColumnVisibility, type ColDef } from '@/components/ui/ColumnPicker';
import { useAsesorEmpresas, useDashboardAsesores } from '@/hooks';
import { useImpersonation } from '@/hooks/useImpersonation';
import { sysadminService } from '@/services/sysadmin.service';
import { AlertTriangle, Check, ExternalLink, LogIn, Mail, MessageCircle, Pencil, Phone, X } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

// ─── Helpers ─────────────────────────────────────────

function formatHours(h: number): string {
  const t = Math.round(h * 3600);
  const hrs = Math.floor(t / 3600);
  const min = Math.floor((t % 3600) / 60);
  if (hrs === 0) return `${min}m`;
  return `${hrs}h ${min}m`;
}

function getInitials(name: string) {
  return name
    .split(' ')
    .slice(0, 2)
    .map((p) => p[0])
    .join('')
    .toUpperCase();
}

const AVATAR_COLORS = [
  { bg: 'var(--admin-blue-bg)', text: 'var(--admin-blue)' },
  { bg: 'var(--admin-green-bg)', text: 'var(--admin-green)' },
  { bg: 'var(--admin-purple-bg)', text: 'var(--admin-purple)' },
  { bg: 'var(--admin-amber-bg)', text: 'var(--admin-amber)' },
];

function ProgBar({ value, color }: { value: number; color: string }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className="admin-prog-bar">
        <span className="admin-prog-fill" style={{ width: `${Math.min(value, 100)}%`, background: color }} />
      </span>
      <span className="text-[12px] font-medium">{value.toFixed(0)}%</span>
    </span>
  );
}

function pColor(v: number): string {
  if (v >= 70) return 'var(--admin-green)';
  if (v >= 50) return '#e09b00';
  return 'var(--admin-red)';
}

/**
 * Validates a Colombian phone number.
 * Accepts formats: 3001234567, +573001234567, 573001234567
 * Returns the normalized 10-digit local number, or null if invalid.
 */
function parseColombianPhone(raw: string): string | null {
  const clean = raw.replace(/[\s\-()]/g, '');
  const match = clean.match(/^(?:\+?57)?(\d{10})$/);
  return match ? match[1] : null;
}

function isValidPhone(value: string): boolean {
  if (!value.trim()) return true;
  return parseColombianPhone(value) !== null;
}

// ─── Phone Row ────────────────────────────────────────

function PhoneRow({ asesorId, initialPhone, onSaved }: { asesorId: number; initialPhone: string; onSaved: (phone: string) => void }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(initialPhone);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const valid = isValidPhone(value);
  const normalizedPhone = parseColombianPhone(initialPhone);
  const waUrl = normalizedPhone ? `https://wa.me/57${normalizedPhone}` : null;

  const handleEdit = () => {
    setValue(initialPhone);
    setSaveError(null);
    setEditing(true);
  };

  const handleCancel = () => {
    setValue(initialPhone);
    setSaveError(null);
    setEditing(false);
  };

  const handleSave = async () => {
    const trimmed = value.trim();
    if (!isValidPhone(trimmed)) return;
    setSaving(true);
    setSaveError(null);
    try {
      await sysadminService.actualizarAsesor(asesorId, { telefono: trimmed });
      onSaved(trimmed);
      setEditing(false);
    } catch {
      setSaveError('Error al guardar. Intentalo de nuevo.');
    } finally {
      setSaving(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSave();
    if (e.key === 'Escape') handleCancel();
  };

  return (
    <div className="flex flex-col gap-0.5">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="flex items-center gap-1">
          <Phone className="w-3 h-3" />
          {!editing ? (
            <>
              {initialPhone ? (
                <span className="font-medium text-(--admin-text2)">{initialPhone}</span>
              ) : (
                <span className="italic opacity-50">Sin telefono</span>
              )}
              {waUrl && (
                <a
                  href={waUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="ml-1.5 inline-flex items-center gap-0.5 text-[11px] px-1.5 py-0.5 rounded-md font-semibold transition-colors"
                  style={{ background: 'var(--admin-green-bg)', color: 'var(--admin-green)' }}
                  title="Abrir WhatsApp"
                >
                  <MessageCircle className="w-3 h-3" />
                  WhatsApp
                  <ExternalLink className="w-2.5 h-2.5 opacity-60" />
                </a>
              )}
              <button
                type="button"
                onClick={handleEdit}
                className="ml-0.5 p-0.5 rounded hover:bg-(--admin-surface2) transition-colors opacity-40 hover:opacity-100"
                title="Editar telefono"
              >
                <Pencil className="w-2.5 h-2.5" />
              </button>
            </>
          ) : (
            <span className="flex items-center gap-1.5">
              <input
                autoFocus
                type="text"
                value={value}
                onChange={(e) => {
                  setValue(e.target.value);
                  setSaveError(null);
                }}
                onKeyDown={handleKeyDown}
                placeholder="3001234567"
                className={`text-[12px] px-2 py-0.5 rounded border outline-none w-36 transition-colors ${
                  !valid && value.trim()
                    ? 'border-red-400 bg-red-50 text-red-700'
                    : 'border-(--admin-border) bg-(--admin-surface2) text-(--admin-text)'
                }`}
                disabled={saving}
              />
              <button
                type="button"
                onClick={handleSave}
                disabled={saving || (!valid && !!value.trim())}
                className="p-0.5 rounded text-(--admin-green) hover:bg-(--admin-green-bg) transition-colors disabled:opacity-40"
                title="Guardar"
              >
                <Check className="w-3.5 h-3.5" />
              </button>
              <button
                type="button"
                onClick={handleCancel}
                disabled={saving}
                className="p-0.5 rounded text-(--admin-red) hover:bg-(--admin-red-bg) transition-colors"
                title="Cancelar"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </span>
          )}
        </span>
      </div>
      {editing && !valid && value.trim() && (
        <span className="flex items-center gap-1 text-[11px] text-red-600 pl-4">
          <AlertTriangle className="w-3 h-3 shrink-0" />
          Numero invalido. Formato colombiano: 3001234567
        </span>
      )}
      {saveError && (
        <span className="flex items-center gap-1 text-[11px] text-red-600 pl-4">
          <AlertTriangle className="w-3 h-3 shrink-0" />
          {saveError}
        </span>
      )}
    </div>
  );
}

// ─── Component ──────────────────────────────────────

type AsesorEmpresaColKey = 'nombre' | 'nit' | 'norma' | 'horas' | 'progreso' | 'estado';

const ASESOR_EMPRESA_COLS: ColDef<AsesorEmpresaColKey>[] = [
  { key: 'nombre', label: 'Empresa', defaultVisible: true },
  { key: 'nit', label: 'NIT', defaultVisible: true },
  { key: 'norma', label: 'Norma Empresa', defaultVisible: true },
  { key: 'horas', label: '% Horas', defaultVisible: true },
  { key: 'progreso', label: 'Progreso', defaultVisible: true },
  { key: 'estado', label: 'Estado', defaultVisible: true },
];

export function AdminAsesorDetallePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { asesores, isLoading: loadingAsesores } = useDashboardAsesores();
  const { canImpersonate, impersonate } = useImpersonation();
  const [impersonating, setImpersonating] = useState(false);
  const [phone, setPhone] = useState<string | null>(null);
  const { empresas, isLoading: loadingEmpresas } = useAsesorEmpresas(id ?? null);
  const {
    visible: colVisible,
    toggle: toggleCol,
    reset: resetCols,
    activeCount: colCount,
  } = useColumnVisibility<AsesorEmpresaColKey>(ASESOR_EMPRESA_COLS, 'tourixia:admin-asesor-detalle-empresas-cols');

  const asesor = useMemo(() => asesores.find((a) => String(a.asesorId) === id), [asesores, id]);
  const idx = useMemo(() => asesores.findIndex((a) => String(a.asesorId) === id), [asesores, id]);

  const colors = AVATAR_COLORS[(idx >= 0 ? idx : 0) % AVATAR_COLORS.length];
  const currentPhone = phone !== null ? phone : (asesor?.telefono ?? '');

  const handleImpersonate = async () => {
    if (!asesor) return;
    setImpersonating(true);
    try {
      await impersonate(Number(asesor.asesorId));
      window.location.replace('/');
    } catch {
      /* ignore */
    } finally {
      setImpersonating(false);
    }
  };

  if (loadingAsesores) {
    return (
      <div className="p-12 text-center">
        <div className="w-6 h-6 border-2 border-(--admin-blue) border-t-transparent rounded-full animate-spin mx-auto" />
      </div>
    );
  }

  if (!asesor) {
    return (
      <div className="p-12 text-center">
        <p className="text-(--admin-text3) mb-2">Asesor no encontrado</p>
        <button type="button" className="admin-btn" onClick={() => navigate('/asesores')}>
          ← Volver a asesores
        </button>
      </div>
    );
  }

  return (
    <>
      {/* Header Panel */}
      <div className="admin-detail-panel">
        <div className="flex items-start gap-4 mb-5">
          <div className="admin-avatar text-[18px]" style={{ background: colors.bg, color: colors.text, width: 56, height: 56 }}>
            {getInitials(asesor.nombre)}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[18px] font-semibold text-(--admin-text) mb-0.5">{asesor.nombre}</div>
            <div className="text-[12px] text-(--admin-text3) flex flex-col gap-1.5">
              <span className="flex items-center gap-1">
                <Mail className="w-3 h-3" /> {asesor.correo}
              </span>
              <PhoneRow asesorId={Number(asesor.asesorId)} initialPhone={currentPhone} onSaved={(p) => setPhone(p)} />
            </div>
          </div>
          <div className="flex gap-2 shrink-0 flex-wrap">
            <span className="admin-chip bg-(--admin-green-bg) text-(--admin-green)">Activo</span>
            <span className="admin-chip bg-(--admin-blue-bg) text-(--admin-blue)">{asesor.totalEmpresas} empresas</span>
          </div>
        </div>

        <div className="admin-detail-grid">
          <div className="admin-detail-field">
            <div className="text-[10px] text-(--admin-text3) uppercase tracking-wider mb-1">Asesorias finalizadas</div>
            <div className="text-[15px] font-semibold">{asesor.asesoriasFinalizadas}</div>
          </div>
          <div className="admin-detail-field">
            <div className="text-[10px] text-(--admin-text3) uppercase tracking-wider mb-1">Asesorias activas</div>
            <div className="text-[15px] font-semibold">{asesor.asesoriasActivas}</div>
          </div>
          <div className="admin-detail-field">
            <div className="text-[10px] text-(--admin-text3) uppercase tracking-wider mb-1">Avance de Horas</div>
            <div
              className="text-[15px] font-semibold cursor-help hover:text-(--admin-blue) transition-colors"
              title={`${formatHours(asesor.horasTotales)} / ${formatHours(asesor.totalEmpresas * 320)}`}
            >
              {asesor.totalEmpresas > 0 ? ((asesor.horasTotales / (asesor.totalEmpresas * 320)) * 100).toFixed(1) : '0.0'}%
            </div>
          </div>
          <div className="admin-detail-field">
            <div className="text-[10px] text-(--admin-text3) uppercase tracking-wider mb-1">Cumpl. promedio</div>
            <div className="text-[15px] font-semibold">{asesor.cumplimientoPromedio.toFixed(1)}%</div>
          </div>
          <div className="admin-detail-field">
            <div className="text-[10px] text-(--admin-text3) uppercase tracking-wider mb-1">Empresas activas</div>
            <div className="text-[15px] font-semibold">{asesor.totalEmpresas}</div>
          </div>
          <div className="admin-detail-field">
            <div className="text-[10px] text-(--admin-text3) uppercase tracking-wider mb-1">Rol</div>
            <div className="text-[15px] font-semibold">ASESOR</div>
          </div>
        </div>

        {canImpersonate && (
          <button
            type="button"
            onClick={handleImpersonate}
            disabled={impersonating}
            className="admin-btn flex items-center gap-2 text-[12px] bg-(--admin-amber-bg) text-(--admin-amber) border-transparent hover:opacity-80 disabled:opacity-50"
          >
            <LogIn className="w-3.5 h-3.5" />
            {impersonating ? 'Accediendo...' : 'Acceder como este asesor'}
          </button>
        )}
      </div>

      {/* Empresas Table */}
      <div className="admin-card">
        <div className="admin-card-header flex items-center justify-between">
          <div className="text-[13px] font-semibold text-(--admin-text) flex items-center gap-2">
            Empresas asignadas
            <span className="bg-(--admin-surface2) px-1.5 py-0.5 rounded text-(--admin-text3) font-normal text-[11px]">
              {empresas.length}
            </span>
          </div>
          <ColumnPicker cols={ASESOR_EMPRESA_COLS} visible={colVisible} onToggle={toggleCol} onReset={resetCols} activeCount={colCount} />
        </div>
        {loadingEmpresas ? (
          <div className="p-8 text-center">
            <div className="w-5 h-5 border-2 border-(--admin-blue) border-t-transparent rounded-full animate-spin mx-auto" />
          </div>
        ) : (
          <table className="admin-table">
            <thead>
              <tr>
                {colVisible.nombre && <th className="text-left">Empresa</th>}
                {colVisible.nit && <th className="text-left">NIT</th>}
                {colVisible.norma && <th className="text-left">Norma Empresa</th>}
                {colVisible.horas && <th className="text-center">% Horas</th>}
                {colVisible.progreso && <th className="text-center">Progreso</th>}
                {colVisible.estado && <th className="text-right">Estado</th>}
              </tr>
            </thead>
            <tbody>
              {empresas.map((emp) => {
                const c = emp.porcentajeCumplimiento ?? 0;
                const prog = emp.progresoDetallado;
                const status = emp.estadoRelacion || (emp.asesoriaActivaId ? 'EN_CURSO' : 'PENDIENTE');

                return (
                  <tr key={emp.nitId} className="clickable" onClick={() => navigate(`/empresas/${emp.nitId}`)}>
                    {colVisible.nombre && <td className="font-medium text-(--admin-text)">{emp.nombre}</td>}
                    {colVisible.nit && <td className="font-mono text-[11px] text-(--admin-text3)">{emp.nitId}</td>}
                    {colVisible.norma && (
                      <td>
                        <span className="admin-chip bg-(--admin-blue-bg) text-(--admin-blue) text-[10px]">
                          {emp.combinacionNtc?.nombre ?? 'Sin NTC'}
                        </span>
                      </td>
                    )}
                    {colVisible.horas && (
                      <td
                        className="text-center font-medium cursor-help hover:text-(--admin-blue) transition-colors"
                        title={`${formatHours(emp.horasAsesoria)} / 320h`}
                      >
                        {((emp.horasAsesoria / 320) * 100).toFixed(1)}%
                      </td>
                    )}
                    {colVisible.progreso && (
                      <td className="min-w-40">
                        <div className="flex flex-col gap-1.5 py-1">
                          <ProgBar value={c} color={pColor(c)} />
                          {prog && (
                            <div className="flex items-center gap-2 text-[10px] text-(--admin-text3) font-medium">
                              <span className="text-(--admin-green)">C:{prog.C}</span>
                              <span className="text-(--admin-red)">NC:{prog.NC}</span>
                              <span className="text-(--admin-amber)">CP:{prog.CP}</span>
                              <span className="opacity-50">NA:{prog.NA}</span>
                            </div>
                          )}
                        </div>
                      </td>
                    )}
                    {colVisible.estado && (
                      <td className="text-right">
                        {status === 'EN_CURSO' && (
                          <span className="admin-chip bg-(--admin-blue-bg) text-(--admin-blue) animate-pulse border-(--admin-blue-bg)">
                            En curso
                          </span>
                        )}
                        {status === 'EN_REVISION' && (
                          <span className="admin-chip bg-(--admin-amber-bg) text-(--admin-amber)">En revision</span>
                        )}
                        {status === 'FINALIZADA' && (
                          <span className="admin-chip bg-(--admin-green-bg) text-(--admin-green)">Finalizada</span>
                        )}
                        {status === 'PENDIENTE' && <span className="admin-chip bg-(--admin-surface2) text-(--admin-text2)">Pendiente</span>}
                      </td>
                    )}
                  </tr>
                );
              })}
              {empresas.length === 0 && (
                <tr>
                  <td colSpan={6} className="text-center py-6 text-(--admin-text3)">
                    Sin empresas asignadas
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}
